<script setup lang="ts">
import { toast } from "vue-sonner";
import {
  getNavigationPageDisplayStateReferences,
} from "../../../lib/navigationPageState";
import type {
  NavigationPageDisplayState,
  NavigationPageItem,
  NavigationPageScale,
} from "../../../types/blocks/navigation/NavigationPageOption";
import { resolveLayers } from "./editorResolve";
import { editorDisplayStates } from "./editorResolve";
import {
  NAV_FIELDS,
  getField,
  isSet,
  seedFieldValue,
  setField,
  unsetField,
  type FieldDef,
} from "./fieldRegistry";
import LayerEditRow from "./LayerEditRow.vue";
import ResolvedCardPreview from "./ResolvedCardPreview.vue";
import SetFieldMenu from "./SetFieldMenu.vue";

const props = defineProps<{
  blockId: string;
  open: boolean;
  initialStateId?: string;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
}>();

const { t } = useI18n({ useScope: "local" });
const { block, field, patch } = useInlineBlockEditor(props.blockId);

const dialogOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit("update:open", value),
});

const scale = field<NavigationPageScale>("scale", "md");
const items = computed<NavigationPageItem[]>(() =>
  Array.isArray(block.value?.items) ? block.value.items : [],
);
const displayStates = computed<NavigationPageDisplayState[]>(() =>
  editorDisplayStates(block.value?.displayStates),
);
const selectedStateId = shallowRef<string>("");
const selectedStateIndex = computed(() =>
  displayStates.value.findIndex((state) => state.id === selectedStateId.value),
);
const selectedState = computed(() =>
  selectedStateIndex.value >= 0 ? displayStates.value[selectedStateIndex.value] : null,
);
const setFields = computed(() =>
  selectedState.value
    ? NAV_FIELDS.filter((field) => isSet(getField(selectedState.value, field)))
    : [],
);
const unsetFields = computed(() =>
  selectedState.value
    ? NAV_FIELDS.filter((field) => !isSet(getField(selectedState.value, field)))
    : [],
);
const usageReferences = computed(() =>
  selectedState.value
    ? getNavigationPageDisplayStateReferences(items.value, selectedState.value.id)
    : [],
);
const previewResolved = computed(() => {
  const state = selectedState.value;
  if (!state) return { classes: {} };
  const firstItem = items.value[0];
  const sample = {
    title: state.title || firstItem?.title || t("preview.title"),
    description: state.description || firstItem?.description || t("preview.description"),
    icon: state.icon || firstItem?.icon || "lucide:circle-arrow-right",
    image: state.image || firstItem?.image || "",
    url: state.url || state.to || firstItem?.url || firstItem?.to || "/preview",
    to: state.url || state.to || firstItem?.url || firstItem?.to || "/preview",
    external: state.external ?? firstItem?.external ?? false,
    interactive: state.interactive ?? firstItem?.interactive ?? true,
    classes: {
      ...(state.classes ?? {}),
    },
  };

  return resolveLayers([{ key: "base", label: state.name, src: sample }]).resolved;
});

watch(
  [() => props.open, () => props.initialStateId],
  ([open, initialStateId]) => {
    if (!open) return;
    selectedStateId.value = (initialStateId || selectedStateId.value)
      ?? displayStates.value[0]?.id
      ?? "";
    if (!displayStates.value.some((state) => state.id === selectedStateId.value)) {
      selectedStateId.value = displayStates.value[0]?.id ?? "";
    }
  },
  { immediate: true },
);

watch(displayStates, (states) => {
  if (
    props.open
    && !states.some((state) => state.id === selectedStateId.value)
  ) {
    selectedStateId.value = states[0]?.id ?? "";
  }
});

function cloneState(state: NavigationPageDisplayState): NavigationPageDisplayState {
  return {
    ...state,
    classes: state.classes ? { ...state.classes } : undefined,
  };
}

function writeStates(states: NavigationPageDisplayState[]) {
  patch({ displayStates: states });
}

function updateSelectedState(
  transform: (draft: NavigationPageDisplayState) => NavigationPageDisplayState,
) {
  if (selectedStateIndex.value < 0) return;
  writeStates(displayStates.value.map((state, index) =>
    index === selectedStateIndex.value ? transform(cloneState(state)) : state));
}

function addState() {
  const newState: NavigationPageDisplayState = {
    id: crypto.randomUUID(),
    name: t("defaultStateName", { number: displayStates.value.length + 1 }),
    classes: {},
  };
  writeStates([...displayStates.value, newState]);
  selectedStateId.value = newState.id;
}

function moveState(offset: -1 | 1) {
  const from = selectedStateIndex.value;
  const to = from + offset;
  if (from < 0 || to < 0 || to >= displayStates.value.length) return;

  const nextStates = [...displayStates.value];
  const [moved] = nextStates.splice(from, 1);
  nextStates.splice(to, 0, moved);
  writeStates(nextStates);
}

function deleteState() {
  const state = selectedState.value;
  if (!state) return;
  const references = getNavigationPageDisplayStateReferences(items.value, state.id);
  if (references.length) {
    toast.error(
      t("referencedError", {
        name: state.name,
        references: references.slice(0, 3).join(", "),
        remainder: references.length > 3
          ? t("referenceRemainder", { count: references.length - 3 })
          : "",
      }),
    );
    return;
  }

  const nextStates = displayStates.value.filter((entry) => entry.id !== state.id);
  writeStates(nextStates);
  selectedStateId.value = nextStates[0]?.id ?? "";
}

function renameState(value: unknown) {
  updateSelectedState((draft) => ({
    ...draft,
    name: String(value ?? ""),
  }));
}

function writeField(def: FieldDef, value: unknown) {
  updateSelectedState((draft) => setField(draft, def, value));
}

function removeField(def: FieldDef) {
  updateSelectedState((draft) => unsetField(draft, def));
}

function usageCount(references: string[]) {
  return references.length === 1
    ? t("usage.one")
    : t("usage.many", { count: references.length });
}

function seedField(def: FieldDef) {
  writeField(def, seedFieldValue(def, getField(previewResolved.value, def)));
}

function stateSwatchClasses(state: NavigationPageDisplayState) {
  return state.classes?.card || "bg-background";
}
</script>

<template>
  <Dialog v-model:open="dialogOpen">
    <DialogContent
      class="grid-rows-[auto_minmax(0,1fr)_auto] max-h-[88dvh] gap-0 p-0 sm:max-w-[940px]"
    >
      <DialogHeader class="border-b px-6 py-4 text-left">
        <DialogTitle class="flex items-center gap-2 text-[15px]">
          <span
            class="grid size-8 place-items-center rounded-lg border"
            style="background-color: color-mix(in oklch, var(--cat-navigation) 13%, transparent); color: color-mix(in oklch, var(--cat-navigation) 72%, black); border-color: color-mix(in oklch, var(--cat-navigation) 28%, transparent);"
          >
            <Icon mode="svg" size="16" name="lucide:swatch-book" />
          </span>
          {{ t("title") }}
        </DialogTitle>
        <DialogDescription>
          {{ t("description") }}
        </DialogDescription>
      </DialogHeader>

      <div class="grid min-h-0 grid-cols-[236px_minmax(0,1fr)]">
        <div class="min-h-0 overflow-y-auto border-r bg-[var(--canvas)] px-3 py-4">
          <div class="space-y-1.5">
            <button
              v-for="state in displayStates"
              :key="state.id"
              type="button"
              class="flex w-full items-center gap-2 rounded-md border px-2 py-2 text-left hover:bg-accent/40"
              :class="selectedStateId === state.id ? 'border-[var(--cat-navigation)] bg-[color-mix(in_oklch,var(--cat-navigation)_8%,transparent)]' : ''"
              @click="selectedStateId = state.id"
            >
              <span :class="['size-[18px] shrink-0 rounded-md border', stateSwatchClasses(state)]" />
              <span class="min-w-0 flex-1">
                <span class="block truncate text-[12.5px] font-semibold">{{ state.name }}</span>
                <span class="block truncate text-[10px] text-muted-foreground">
                  {{ usageCount(getNavigationPageDisplayStateReferences(items, state.id)) }}
                </span>
              </span>
            </button>

            <Button
              type="button"
              variant="outline"
              class="h-8 w-full rounded-md border-dashed text-[11px] font-semibold"
              @click="addState"
            >
              <Icon mode="svg" size="12" name="lucide:plus" class="mr-1" />
              {{ t("add") }}
            </Button>
          </div>
        </div>

        <div class="min-h-0 overflow-y-auto px-6 py-4">
          <div v-if="selectedState" class="space-y-4">
            <div class="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
              <ResolvedCardPreview :resolved="previewResolved" :scale="scale" />

              <div class="space-y-3 rounded-lg border bg-card p-4">
                <div class="flex items-start gap-2">
                  <div class="min-w-0 flex-1 space-y-2">
                    <EditorField :label="t('fields.name')">
                      <Input
                        :model-value="selectedState.name"
                        class="h-8 rounded-sm bg-background text-[12.5px]"
                        @update:model-value="renameState"
                      />
                    </EditorField>
                    <div>
                      <div class="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {{ t("fields.id") }}
                      </div>
                      <code class="mt-1 inline-flex rounded bg-secondary px-1.5 py-0.5 font-mono text-[10.5px]">
                        {{ selectedState.id }}
                      </code>
                    </div>
                  </div>
                  <div class="flex shrink-0 items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      class="size-8"
                      :disabled="selectedStateIndex <= 0"
                      @click="moveState(-1)"
                    >
                      <Icon mode="svg" size="14" name="lucide:arrow-up" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      class="size-8"
                      :disabled="selectedStateIndex >= displayStates.length - 1"
                      @click="moveState(1)"
                    >
                      <Icon mode="svg" size="14" name="lucide:arrow-down" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      class="size-8 text-destructive"
                      @click="deleteState"
                    >
                      <Icon mode="svg" size="14" name="lucide:trash-2" />
                    </Button>
                  </div>
                </div>

                <div>
                  <div class="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {{ t("usedBy") }}
                  </div>
                  <div v-if="usageReferences.length" class="mt-2 flex flex-wrap gap-1.5">
                    <code
                      v-for="reference in usageReferences"
                      :key="reference"
                      class="rounded bg-secondary px-1.5 py-0.5 font-mono text-[10.5px]"
                    >
                      {{ reference }}
                    </code>
                  </div>
                  <p v-else class="mt-2 text-[11px] text-muted-foreground">
                    {{ t("unused") }}
                  </p>
                </div>
              </div>
            </div>

            <div class="space-y-2 rounded-lg border bg-card p-4">
              <div class="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {{ t("setsFields") }}
              </div>
              <LayerEditRow
                v-for="def in setFields"
                :key="def.key"
                :def="def"
                :model-value="getField(selectedState, def)"
                @update:model-value="(value) => writeField(def, value)"
                @unset="removeField(def)"
              />
              <p v-if="!setFields.length" class="text-[11px] text-muted-foreground">
                {{ t("empty") }}
              </p>
              <SetFieldMenu
                tone="variant"
                :available="unsetFields"
                :label="t('setAnotherField')"
                @pick="seedField"
              />
            </div>
          </div>
        </div>
      </div>

      <DialogFooter class="items-center border-t px-6 py-4 sm:justify-between">
        <p class="text-[11px] text-muted-foreground">
          {{ t("footer") }}
        </p>
        <Button type="button" @click="dialogOpen = false">{{ t("done") }}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<i18n lang="yaml">
en:
  title: "Display states"
  description: "Reusable looks that items and variants reference by name. A state only lists the fields it sets."
  add: "Add display state"
  done: "Done"
  defaultStateName: "State {number}"
  fields:
    name: "Name"
    id: "ID"
  usedBy: "Used by"
  unused: "Not referenced yet — safe to edit or delete."
  setsFields: "Sets these fields"
  empty: "This state sets nothing yet."
  setAnotherField: "Set another field"
  preview:
    title: "Preview link"
    description: "Representative content for this display state."
  usage:
    one: "1 use"
    many: "{count} uses"
  footer: "States used by an item or variant can't be deleted. Edits apply live."
  referencedError: "{name} is still used by {references}{remainder}."
  referenceRemainder: " and {count} more"
</i18n>
